import axios from "axios";
import { config } from "../config";
import { userService } from "./UserService";
import { GoogleSpreadsheet } from "google-spreadsheet";
import { showGlobalErrorToast, showGlobalSuccessToast } from "../utils/GlobalToast";
import { normalizeCalibrationDttm } from "../components/custom/calibrationPlatform/util";

export const DEFAULT_GROUP_KEY = "default";
const COLUMN_RENAMES = {
    structures: {
        "Structure Name": "structure_name",
        Lat: "latitude",
        Lon: "longitude",
        "Lowest Conductor Attachment Elevation": "min_cond_elevation",
        "Ground Elevation": "ground_elevation",
        "Dead-End": "dead_end",
    },
    spans: {
        "Section Name": "section_name",
        "Start Structure": "start_structure",
        "End Structure": "end_structure",
        "Mid-Span Ground Elevation": "mid_level_ground_elevation",
        "Mid-Span Lowest Cond Elevation": "mid_span_lowest_cond_elevation",
        "Start Dead-End": "start_dead_end",
        "End Dead-End": "end_dead_end",
        "Start Attachment Elevation": "start_attachment_elevation",
        "End Attachment Elevation": "end_attachment_elevation",
    },
    sections: {
        "Section Name": "section_name",
        "Start Structure": "start_structure",
        "End Structure": "end_structure",
        "Timezone Code": "timezone_code",
        "Line Name": "line_name",
        "Line Voltage": "line_voltage",
        MOT: "maximum_operating_temperature",
        "Conductor Name": "conductor_name",
        "Conductor Type": "conductor_type",
        "Circuit Orientation": "circuit_orientation",
        "Num Bundled Subconductors": "num_bundled_subconductors",
        Absorptivity: "absorptivity",
        Emissivity: "emissivity",
        "Product Type": "product_type",
    },
};
export const KMZ_DEFAULT_PROJECT_NAME = "kmz";

const toDisplayableError = (errorMessage) => {
    if (typeof errorMessage === "string") return errorMessage;
    if (errorMessage?.name === "AxiosError") return errorMessage;
    return JSON.stringify(errorMessage);
};

const handleError = (error, processTitle, { statusCodesToIgnore = [], throws = "message", showToast = true } = {}) => {
    if (statusCodesToIgnore.includes(error?.response?.status)) return;
    const errorMessage = error?.response?.data?.error || error?.response?.data?.message || error?.message || error;
    console.error(`Error while ${processTitle}: ${toDisplayableError(errorMessage)}`);
    if (showToast) {
        showGlobalErrorToast(`Error while ${processTitle}: ${toDisplayableError(errorMessage)}` || "An error occurred");
    }
    if (throws === "message") {
        throw errorMessage;
    } else if (throws === "error") {
        throw error;
    }
};

const handleTimeoutError = (error, processTitle, timeoutMessage) => {
    if (error?.response?.status === 504 || (error?.message === "Network Error" && !error?.response)) {
        console.error(`Error while ${processTitle}: ${timeoutMessage}`);
        throw timeoutMessage;
    }
};

const renameAndFormatRow = (rowData, headers, renames) => {
    return headers.reduce((newRow, header, columnIndex) => {
        if (header in renames) {
            const newHeaderName = renames[header];
            // Null trailing values are omitted, so we add them back in here
            newRow[newHeaderName] = columnIndex < rowData.length ? rowData[columnIndex] : null;
            newRow[newHeaderName] = newRow[newHeaderName] === "" ? null : newRow[newHeaderName];
        }
        return newRow;
    }, {});
};

// Format the data from google-spreadsheet to be ready for ingress
const extractRecordsFromSheet = (sheetObj, renames) => {
    return sheetObj.map((row) => {
        const rowData = row._rawData;
        const headers = row._worksheet._headerValues;
        return renameAndFormatRow(rowData, headers, renames);
    });
};

export class CalibrationService {
    getRequestHeaders() {
        return {
            Authorization: `${userService.getAuthToken()}`,
            "Content-Type": "application/json",
        };
    }

    async clientAssetDataIngress(clientName, projectName, clientAssetData) {
        try {
            const body = {
                client_name: clientName,
                project_name: projectName,
                inputs: [],
                email_list: [JSON.parse(localStorage.getItem("user")).email],
                extra: clientAssetData,
            };

            const response = await axios.post(
                `${config.calibrationPlatformAPIUrl}/processes/client-asset-data-ingress`,
                body,
                {
                    headers: this.getRequestHeaders(),
                }
            );

            return response;
        } catch (error) {
            handleError(error, "uploading CAD Data");
        }
    }

    async pitGeojsonIngress(clientName, projectName, gdriveFileId, gdriveAccessToken) {
        try {
            const body = {
                client_name: clientName,
                project_name: projectName,
                inputs: [],
                email_list: [JSON.parse(localStorage.getItem("user")).email],
                extra: {
                    gdrive_file_id: gdriveFileId,
                    gdrive_access_token: gdriveAccessToken,
                },
            };

            const response = await axios.post(
                `${config.calibrationPlatformAPIUrl}/processes/pit-geojson-ingress`,
                body,
                {
                    headers: this.getRequestHeaders(),
                }
            );

            return response;
        } catch (error) {
            handleError(error, "triggering PIT GeoJSON ingress");
        }
    }

    async redrivePitGeojson(clientName, projectName, rawDatasetType, rawVersionDttm) {
        try {
            const body = {
                client_name: clientName,
                project_name: projectName,
                inputs: [],
                email_list: [JSON.parse(localStorage.getItem("user")).email],
                // The backend switches to redrive when raw_dataset_type is present; it reprocesses the
                // whole stored source for this client/project (no group key).
                extra: {
                    raw_dataset_type: rawDatasetType,
                    // Omit raw_version_dttm entirely to let the backend redrive the latest version.
                    ...(rawVersionDttm ? { raw_version_dttm: rawVersionDttm } : {}),
                },
            };

            const response = await axios.post(
                `${config.calibrationPlatformAPIUrl}/processes/pit-geojson-ingress`,
                body,
                {
                    headers: this.getRequestHeaders(),
                }
            );

            return response;
        } catch (error) {
            handleError(error, "redriving PIT GeoJSON");
        }
    }

    async plscaddKmzIngress(clientName, gdriveFileId, gdriveAccessToken) {
        try {
            const body = {
                client_name: clientName,
                gdrive_file_id: gdriveFileId,
                gdrive_access_token: gdriveAccessToken,
                email_list: [JSON.parse(localStorage.getItem("user")).email],
            };

            const response = await axios.post(
                `${config.calibrationPlatformAPIUrl}/plscadd-kmz-ingress`,
                body,
                {
                    headers: this.getRequestHeaders(),
                }
            );

            return response;
        } catch (error) {
            handleError(error, "uploading PLS-CADD KMZ");
        }
    }

    async plscaddKmzConfigUpload(clientName, projectName, configs) {
        try {
            const body = {
                client_name: clientName,
                project_name: projectName,
                configs: configs,
                email_list: [JSON.parse(localStorage.getItem("user")).email],
            };

            const response = await axios.post(
                `${config.calibrationPlatformAPIUrl}/plscadd-kmz-config`,
                body,
                {
                    headers: this.getRequestHeaders(),
                }
            );

            return response;
        } catch (error) {
            handleError(error, "saving PLS-CADD KMZ config");
        }
    }

    async plscaddKmzToRootGeojson(clientName, outputProjectName, inputs) {
        try {
            const body = {
                client_name: clientName,
                project_name: KMZ_DEFAULT_PROJECT_NAME,
                inputs: inputs,
                email_list: [JSON.parse(localStorage.getItem("user")).email],
                extra: {
                    output_project_name: outputProjectName,
                },
            };

            const response = await axios.post(
                `${config.calibrationPlatformAPIUrl}/processes/plscadd-kmz-to-root-geojson`,
                body,
                {
                    headers: this.getRequestHeaders(),
                }
            );

            return response;
        } catch (error) {
            handleError(error, "triggering PLS-CADD KMZ to RootGeojson");
        }
    }

    /**
     * Kick off StructureMilesFromRootGeojson for one client/project.
     *
     * root-geojson is a required input for this process, and cal-plat's get_single_dataset
     * raises ValueError when a required input's version_dttm is null, so callers must pass an
     * explicit version. group_key is not a parameter because the backend hardcodes "default"
     * for this process.
     *
     * The route runs the whole computation synchronously inside the API lambda, behind API
     * Gateway's 29s window, so a large project legitimately returns 504 (or surfaces as a bare
     * "Network Error") while the run finishes server-side. We classify that shape as submitted
     * and resolve null BEFORE calling handleError, because handleError console.errors
     * unconditionally and src/CLAUDE.md requires the app to run without console output.
     *
     * Genuine failures rethrow the AxiosError rather than the default bare-string message,
     * because the caller needs error.response.data to surface cal-plat's reason -- the error
     * middleware puts it under value_error / key_error / client_error depending on the
     * exception, not always under "error". showToast is false so the caller owns its own
     * messaging.
     */
    async triggerStructureMilesFromRootGeojson(clientName, projectName, rootGeojsonVersionDttm) {
        try {
            const body = {
                client_name: clientName,
                project_name: projectName,
                inputs: [
                    {
                        dataset_type: "root-geojson",
                        group_key: DEFAULT_GROUP_KEY,
                        version_dttm: normalizeCalibrationDttm(rootGeojsonVersionDttm),
                    },
                ],
                email_list: [JSON.parse(localStorage.getItem("user")).email],
                extra: {},
            };

            const response = await axios.post(
                `${config.calibrationPlatformAPIUrl}/processes/structure-miles-from-root-geojson`,
                body,
                {
                    headers: this.getRequestHeaders(),
                }
            );

            return response;
        } catch (error) {
            const isGatewayTimeout =
                error?.response?.status === 504 ||
                error?.code === "ERR_NETWORK" ||
                (error?.message === "Network Error" && !error?.response);
            if (isGatewayTimeout) return null;
            handleError(error, "triggering StructureMilesFromRootGeojson", {
                throws: "error",
                showToast: false,
            });
        }
    }

    async triggerPitPipeline(clientName, projectName, extra, inputs) {
        try {
            const body = {
                client_name: clientName,
                project_name: projectName,
                inputs: inputs,
                email_list: [JSON.parse(localStorage.getItem("user")).email],
                extra: extra,
            };

            const response = await axios.post(
                `${config.calibrationPlatformAPIUrl}/processes/trigger-pit-pipeline`,
                body,
                {
                    headers: this.getRequestHeaders(),
                }
            );

            return response;
        } catch (error) {
            handleError(error, "triggering PIT pipeline");
        }
    }

    async triggerTileGeneration(clientName, projectName, { additionalProjects = [], outputProjectName = null } = {}) {
        const body = {
            client_name: clientName,
            project_name: projectName,
            email_list: [JSON.parse(localStorage.getItem("user")).email],
        };
        const extra = {};
        if (additionalProjects.length > 0) {
            extra.additional_pit_geojsons = additionalProjects.map(({ clientName: c, projectName: p }) => ({
                client_name: c,
                project_name: p,
            }));
        }
        if (outputProjectName) {
            extra.output_project_name = outputProjectName;
        }
        if (Object.keys(extra).length > 0) {
            body.extra = extra;
        }
        try {
            const response = await axios.post(
                `${config.calibrationPlatformAPIUrl}/processes/trigger-tile-generation`,
                body,
                {
                    headers: this.getRequestHeaders(),
                }
            );
            return response;
        } catch (error) {
            handleError(error, "triggering tile generation", { throws: "error" });
        }
    }

    async getGoogleSheet(sheetId, authToken) {
        try {
            const googleSheet = new GoogleSpreadsheet(sheetId, { token: authToken });
            await googleSheet.loadInfo();

            const sheetTitles = ["Structures", "Sections", "Spans"];

            const [structuresSheet, sectionsSheet, spansSheet] = await Promise.all(
                sheetTitles.map((sheetTitle) => {
                    const sheet = googleSheet.sheetsByTitle[sheetTitle];
                    if (!sheet) {
                        throw new Error(`No sheet found with the title of "${sheetTitle}"`);
                    }
                    return sheet.getRows();
                })
            );

            return {
                structures: extractRecordsFromSheet(structuresSheet, COLUMN_RENAMES["structures"]),
                sections: extractRecordsFromSheet(sectionsSheet, COLUMN_RENAMES["sections"]),
                spans: extractRecordsFromSheet(spansSheet, COLUMN_RENAMES["spans"]),
            };
        } catch (error) {
            handleError(error, "fetching Google Sheet", { throws: "error" });
        }
    }

    async getProcessTypes() {
        try {
            const result = await axios.get(`${config.calibrationPlatformAPIUrl}/process-types/`, {
                headers: this.getRequestHeaders(),
            });

            return result.data;
        } catch (error) {
            handleError(error, "fetching process types", { throws: "error" });
        }
    }

    async getCompletedProcesses(client, project, processTypes = [], startDttm = null, timeBeforeStartInDays = null) {
        try {
            const params = {
                startDttm: startDttm,
                timeBeforeStartInDays: timeBeforeStartInDays,
            };
            if (processTypes.length > 0) {
                params.process_types = processTypes.join(",");
            }
            const result = await axios.get(
                `${config.calibrationPlatformAPIUrl}/clients/${client}/projects/${project}/completed/`,
                {
                    headers: this.getRequestHeaders(),
                    params: params,
                }
            );

            return result.data;
        } catch (error) {
            handleError(error, "fetching completed processes", { throws: "error" });
        }
    }

    async getClients() {
        try {
            const result = await axios.get(`${config.calibrationPlatformAPIUrl}/clients/`, {
                headers: this.getRequestHeaders(),
            });

            return result.data;
        } catch (error) {
            handleError(error, "fetching clients", { showToast: false, throws: "error" });
        }
    }

    async getProjectNames(client) {
        try {
            const result = await axios.get(`${config.calibrationPlatformAPIUrl}/clients/${client}/projects/`, {
                headers: this.getRequestHeaders(),
            });

            if (result.data && Array.isArray(result.data.data)) {
                result.data.data = result.data.data.filter((project) => project !== KMZ_DEFAULT_PROJECT_NAME);
            }

            return result.data;
        } catch (error) {
            handleError(error, `fetching projects for client ${client}`, { showToast: false, throws: "error" });
        }
    }
    async getDatasetTypes(client, project) {
        try {
            const result = await axios.get(
                `${config.calibrationPlatformAPIUrl}/clients/${client}/projects/${project}/dataset-types/`,
                {
                    headers: this.getRequestHeaders(),
                }
            );

            return result.data;
        } catch (error) {
            handleError(error, `fetching dataset types for client ${client}, project ${project}`, {
                showToast: false,
                throws: "error",
            });
        }
    }
    async getGroups(client, project, datasetType) {
        try {
            const result = await axios.get(
                `${config.calibrationPlatformAPIUrl}/clients/${client}/projects/${project}/dataset-types/${datasetType}/groups/`,
                {
                    headers: this.getRequestHeaders(),
                }
            );

            return result.data;
        } catch (error) {
            handleError(error, `fetching groups for client ${client}, project ${project}`, { showToast: false, throws: "error" });
        }
    }

    async getFileNames(client, project, groupKey, datasetType) {
        try {
            const result = await axios.get(
                `${config.calibrationPlatformAPIUrl}/clients/${client}/projects/${project}/dataset-types/${datasetType}/groups/${groupKey}/files/`,
                {
                    headers: this.getRequestHeaders(),
                }
            );
            return result.data;
        } catch (error) {
            handleError(
                error,
                `fetching files for client ${client}, project ${project}, group key ${groupKey} and dataset type ${datasetType}`,
                { showToast: false, throws: "error" }
            );
        }
    }

    getExactDatasetBinaryUrl(client, project, datasetType, groupKey, dttm, relativePath = undefined) {
        // Encode the group key to handle special characters
        const encodedGroupKey = encodeURIComponent(groupKey);
        const path = `${config.calibrationPlatformAPIUrl}/clients/${client}/projects/${project}/dataset-types/${datasetType}/groups/${encodedGroupKey}/files/${dttm}`;
        if (relativePath) {
            return `${path}/${encodeURIComponent(relativePath)}`;
        } else return `${path}?dataOnly=1`;
    }

    async getExactDataset(
        client,
        project,
        groupKey,
        datasetType,
        dttm,
        returnDataOnly = false,
        isBinary = false,
        showErrorToast = false
    ) {
        try {
            // Encode the group key to handle special characters
            const encodedGroupKey = encodeURIComponent(groupKey);

            let url = `${config.calibrationPlatformAPIUrl}/clients/${client}/projects/${project}/dataset-types/${datasetType}/groups/${encodedGroupKey}/files/${dttm}`;
            let options = {
                headers: this.getRequestHeaders(),
            };

            // Certain datasets require us to load them in binary format, so we need to
            // set the responseType to arraybuffer and request the dataOnly query param
            if (returnDataOnly) {
                url += "?dataOnly=1";
            }
            if (isBinary) {
                options.responseType = "arraybuffer";
            }
            const result = await axios.get(url, options);
            return result.data;
        } catch (error) {
            handleError(
                error,
                `fetching exact dataset for client ${client}, project ${project}, group key ${groupKey} dataset type ${datasetType}, and datetime ${dttm}`,
                { showToast: showErrorToast, throws: "error" }
            );
        }
    }

    async getDatasetSignedUrl(client, project, datasetType, groupKey, dttm, { expiresIn, showToast = true } = {}) {
        try {
            const encodedGroupKey = encodeURIComponent(groupKey);
            const params = expiresIn ? { expiresIn } : undefined;
            const url = `${config.calibrationPlatformAPIUrl}/clients/${client}/projects/${project}/dataset-types/${datasetType}/groups/${encodedGroupKey}/files/${normalizeCalibrationDttm(dttm)}/signed-url`;
            const result = await axios.get(url, {
                headers: this.getRequestHeaders(),
                params,
            });
            return result.data;
        } catch (error) {
            handleError(
                error,
                `fetching signed URL for client ${client}, project ${project}, group key ${groupKey}, dataset type ${datasetType}, dttm ${dttm}`,
                { throws: "error", showToast }
            );
        }
    }

    async getLatestDataset(client, project, groupKey, datasetType, showErrorToast = true, dataOnly = false) {
        try {
            // dataOnly returns just the dataset payload (e.g. the raw GeoJSON
            // FeatureCollection for sidecar strategies like root-geojson) instead
            // of the {meta, data} wrapper.
            const url = `${config.calibrationPlatformAPIUrl}/clients/${client}/projects/${project}/dataset-types/${datasetType}/groups/${groupKey}${
                dataOnly ? "?dataOnly=1" : ""
            }`;
            const result = await axios.get(url, {
                headers: this.getRequestHeaders(),
            });
            return result.data;
        } catch (error) {
            // Don't show error toast for 404s - this just means the dataset doesn't exist yet, which is expected for new or incomplete projects
            handleError(
                error,
                `fetching latest dataset for client ${client}, project ${project}, group key ${groupKey} and dataset type ${datasetType}`,
                { showToast: showErrorToast && error?.response?.status !== 404, throws: "error" }
            );
        }
    }

    async getLatestDatasetViaSignedUrl(client, project, groupKey, datasetType, showErrorToast = true) {
        try {
            const fileNames = await this.getFileNames(client, project, groupKey, datasetType);
            const dttm = fileNames?.data?.slice().sort().pop();
            if (!dttm) return null;

            const signed = await this.getDatasetSignedUrl(client, project, datasetType, groupKey, dttm, {
                showToast: showErrorToast,
            });
            if (!signed?.url) return null;

            const result = await axios.get(signed.url);
            return result.data;
        } catch (error) {
            // Don't show error toast for 404s - this just means the dataset doesn't exist yet, which is expected for new or incomplete projects
            handleError(
                error,
                `fetching latest dataset via signed url for client ${client}, project ${project}, group key ${groupKey} and dataset type ${datasetType}`,
                { showToast: showErrorToast && error?.response?.status !== 404, throws: "error" }
            );
        }
    }

    /**
     * Return the latest version_dttm for a dataset type, or null when the project has none.
     *
     * Dataset file basenames are second-precision Z timestamps ("2026-07-21T15:00:00Z"), so a
     * lexical sort is a chronological sort. This is the same resolution
     * getLatestDatasetViaSignedUrl does internally; it gets its own method because triggering a
     * process needs the version_dttm without downloading the payload, and cal-plat's
     * get_single_dataset raises ValueError when a required input is submitted with a null
     * version_dttm.
     *
     * Never rejects. A project with no dataset of this type is an ordinary state rather than an
     * error -- the listing endpoint returns 200 with an empty array for it -- so callers get
     * null and render their own empty state. The catch is there only for genuine transport
     * failures.
     */
    async getLatestVersionDttm(client, project, groupKey, datasetType) {
        const fileNames = await this.getFileNames(client, project, groupKey, datasetType).catch(() => null);
        return fileNames?.data?.slice().sort().pop() ?? null;
    }

    async getAllGroupsFromDttm(client, project, datasetType, dttm) {
        try {
            const result = await axios.get(
                `${config.calibrationPlatformAPIUrl}/clients/${client}/projects/${project}/dataset-types/${datasetType}/files/${dttm}`,
                {
                    headers: this.getRequestHeaders(),
                }
            );
            return result.data;
        } catch (error) {
            handleError(
                error,
                `fetching all groups for client ${client}, project ${project}, dataset type ${datasetType}, and dttm ${dttm}`,
                { throws: "error" }
            );
        }
    }

    async getAllDttmsFromClientProject(client, project, datasetType) {
        try {
            const result = await axios.get(
                `${config.calibrationPlatformAPIUrl}/clients/${client}/projects/${project}/dataset-types/${datasetType}/get-all-dttms/`,
                {
                    headers: this.getRequestHeaders(),
                }
            );
            return result.data;
        } catch (error) {
            handleError(
                error,
                `fetching all dttms for client ${client}, project ${project}, and dataset type ${datasetType}`,
                { throws: "error" }
            );
        }
    }

    async checkNoActivePalmRuns(client, project) {
        try {
            const result = await axios.get(
                `${config.calibrationPlatformAPIUrl}/no-active-palm-runs/client/${client}/project/${project}`,
                {
                    headers: this.getRequestHeaders(),
                }
            );
            return result;
        } catch (error) {
            handleError(error, "checking for active PALM runs", { showToast: false });
        }
    }

    async getPalmTerrainVisualization(client, project, coefficientType, versionDttm) {
        try {
            const result = await axios.get(
                `${config.calibrationPlatformAPIUrl}/get-palm-terrain-viz/clients/${client}/projects/${project}/${coefficientType}/version-dttm/${versionDttm}`,
                {
                    headers: this.getRequestHeaders(),
                }
            );
            return result.data;
        } catch (error) {
            handleError(error, "fetching PALM terrain visualization", { showToast: false, throws: "error" });
        }
    }

    async checkCoefficientDatabaseStatus(client, project, coefficientType, versionDttm) {
        try {
            const result = await axios.get(
                `${config.calibrationPlatformAPIUrl}/check-coefficients-status/clients/${client}/projects/${project}/${coefficientType}/version-dttm/${versionDttm}`,
                {
                    headers: this.getRequestHeaders(),
                }
            );
            return result.data;
        } catch (error) {
            handleError(error, "checking coefficient database status", { showToast: false, throws: "error" });
        }
    }

    async spanCfdCoefficientsTransaction(client, project, dttm, action) {
        try {
            const response = await axios.post(
                `${config.calibrationPlatformAPIUrl}/processes/span-cfd-coefficients-transaction`,
                {
                    client_name: client,
                    project_name: project,
                    inputs: [
                        {
                            dataset_type: "span-palm-coefficients",
                            group_key: DEFAULT_GROUP_KEY,
                            version_dttm: dttm,
                        },
                    ],
                    email_list: [JSON.parse(localStorage.getItem("user")).email],
                    extra: {
                        action: action,
                    },
                },
                {
                    headers: this.getRequestHeaders(),
                }
            );
            return response.data;
        } catch (error) {
            handleTimeoutError(
                error,
                `${action} span CFD coefficients`,
                "Request timed out - the operation is still running. Check back in a few minutes."
            );
            handleError(error, `${action} span CFD coefficients`, { showToast: false });
        }
    }

    async weatherSensorCfdCoefficientsTransaction(client, project, dttm, action) {
        try {
            const response = await axios.post(
                `${config.calibrationPlatformAPIUrl}/processes/weather-site-cfd-coefficients-transaction`,
                {
                    client_name: client,
                    project_name: project,
                    inputs: [
                        {
                            dataset_type: "weather-sensor-palm-coefficients",
                            group_key: DEFAULT_GROUP_KEY,
                            version_dttm: dttm,
                        },
                    ],
                    email_list: [JSON.parse(localStorage.getItem("user")).email],
                    extra: {
                        action: action,
                    },
                },
                {
                    headers: this.getRequestHeaders(),
                }
            );
            return response.data;
        } catch (error) {
            handleTimeoutError(
                error,
                `${action} weather site CFD coefficients`,
                "Request timed out - the operation is still running. Check back in a few minutes."
            );
            handleError(error, `${action} weather site CFD coefficients`, { showToast: false });
        }
    }

    async checkConductorPropertiesExist(client, project, versionDttm) {
        try {
            const result = await axios.get(
                `${config.calibrationPlatformAPIUrl}/check-conductor-properties-exist/clients/${client}/projects/${project}/version-dttm/${versionDttm}`,
                {
                    headers: this.getRequestHeaders(),
                }
            );
            return result.data;
        } catch (error) {
            handleError(error, "checking conductor properties", { showToast: false, throws: "error" });
        }
    }

    async uploadMasterdata(client, project, datasetType, groupKey, dttm) {
        try {
            try {
                // Check if there are any existing datasets in
                // masterdata-records-upload already for this client+project. If
                // that dataset exists, then we need to check if the teardown
                // process has already been run before we can upload.
                const existingDataset = await this.getLatestDataset(
                    client,
                    project,
                    groupKey,
                    "masterdata-records-upload",
                    false
                );
                if (existingDataset?.data && existingDataset.data?.teardown !== "complete") {
                    throw new Error(
                        `Cannot upload! Masterdata upload has already occurred for this client/project/group: ${client}/${project}/${groupKey}`
                    );
                }
            } catch (error) {
                // If we get a 404, then we can proceed with the upload
                if (error?.response?.status !== 404) {
                    throw error;
                }
            }

            // Now we can go ahead with the bulk upload
            const response = await axios.post(
                `${config.calibrationPlatformAPIUrl}/processes/upload-masterdata`,
                {
                    client_name: client,
                    project_name: project,
                    inputs: [
                        {
                            dataset_type: datasetType,
                            group_key: groupKey,
                            version_dttm: dttm,
                        },
                    ],
                    email_list: [JSON.parse(localStorage.getItem("user")).email],
                },
                {
                    headers: this.getRequestHeaders(),
                }
            );
            return response.data;
        } catch (error) {
            handleTimeoutError(
                error,
                "uploading master data",
                "Request timed out - the upload is still running. Check the Progress Viewer under 'MasterdataRecordsUpload' to monitor progress."
            );
            handleError(error, "uploading master data");
        }
    }

    async teardownMasterdata(client, project, datasetType, groupKey, dttm) {
        try {
            // Check if there are any existing datasets in masterdata-records-upload already for this client+project
            const existingUploadsResponse = await this.getFileNames(
                client,
                project,
                groupKey,
                "masterdata-records-upload"
            );
            if (existingUploadsResponse?.data?.length === 0) {
                throw new Error(
                    `Cannot teardown! No files found inside of the S3 bucket to teardown for client+project: ${client}_${project}`
                );
            }

            const response = await axios.post(
                `${config.calibrationPlatformAPIUrl}/processes/teardown-masterdata`,
                {
                    client_name: client,
                    project_name: project,
                    inputs: [{ dataset_type: datasetType, group_key: groupKey, version_dttm: dttm }],
                    email_list: [JSON.parse(localStorage.getItem("user")).email],
                },
                {
                    headers: this.getRequestHeaders(),
                }
            );
            return response.data;
        } catch (error) {
            handleTimeoutError(
                error,
                "tearing down master data",
                "Request timed out - the teardown is still running. Check the Progress Viewer under 'MasterdataRecordsTeardown' to monitor progress."
            );
            handleError(error, "tearing down master data");
        }
    }

    async regenerateMasterdataRecords(client, project) {
        try {
            // First, get all dttm file names for the client-asset-data dataset type
            const clientAssetDataFileNames = await this.getFileNames(
                client,
                project,
                DEFAULT_GROUP_KEY,
                "client-asset-data"
            );

            // This will be list of dttms, so we have to sort and pick the latest
            const latestClientAssetDataDttm = clientAssetDataFileNames.data.sort().pop();

            const response = await axios.post(
                `${config.calibrationPlatformAPIUrl}/processes/generate-md-records`,
                {
                    client_name: client,
                    project_name: project,
                    inputs: [
                        {
                            dataset_type: "client-asset-data",
                            group_key: DEFAULT_GROUP_KEY,
                            version_dttm: latestClientAssetDataDttm,
                        },

                        // Optional input dataset type
                        // In the backend, this will not be loaded in if the dataset does not exist in the first place
                        {
                            dataset_type: "device-data",
                            group_key: DEFAULT_GROUP_KEY,
                        },
                    ],
                    email_list: [JSON.parse(localStorage.getItem("user")).email],
                },
                {
                    headers: this.getRequestHeaders(),
                }
            );
            return response.data;
        } catch (error) {
            handleTimeoutError(
                error,
                "regenerating master data records",
                "Request timed out - regeneration is still running. Check back in a few minutes."
            );
            handleError(error, "regenerating master data records", { showToast: false });
        }
    }

    async pullReferenceRatingMapping(body) {
        try {
            const repsonse = await axios.post(`${config.calibrationPlatformAPIUrl}/reference-rating-combos/`, body, {
                headers: this.getRequestHeaders(),
            });
            return repsonse.data;
        } catch (error) {
            handleError(error, "pulling reference rating mapping");
        }
    }

    async checkifExistingClientRating(body) {
        try {
            const repsonse = await axios.post(
                `${config.calibrationPlatformAPIUrl}/check-existing-client-rating-config/`,
                body,
                {
                    headers: this.getRequestHeaders(),
                }
            );
            return repsonse.data;
        } catch (error) {
            handleError(error, "checking if client rating already exists in the db");
        }
    }

    async updateExistingClientRatingConfig(body) {
        try {
            const repsonse = await axios.post(
                `${config.calibrationPlatformAPIUrl}/update-existing-client-rating-config/`,
                body,
                {
                    headers: this.getRequestHeaders(),
                }
            );
            return repsonse.data;
        } catch (error) {
            handleError(error, "updating client rating config in the db", { showToast: false });
        }
    }

    async UploadClientRatingSiteGroups(client, body) {
        try {
            const repsonse = await axios.post(
                `${config.calibrationPlatformAPIUrl}/client-rating-config-site-group-upload/clients/${client}/`,
                body,
                {
                    headers: this.getRequestHeaders(),
                }
            );
            return { status: repsonse.status, ...repsonse.data };
        } catch (error) {
            if (error?.response?.status === 500 && error?.response?.data?.message) {
                return { status: 500, message: error.response.data.message };
            }
            handleError(error, "uploading client rating and linking site groups", { showToast: false });
        }
    }
    async getSectionMethodologySummary(client, project) {
        try {
            const repsonse = await axios.get(
                `${config.calibrationPlatformAPIUrl}/maple/get/sections/methodology-summary/client/${client}/project/${project}`,
                {
                    headers: this.getRequestHeaders(),
                }
            );
            return repsonse.data;
        } catch (error) {
            handleError(error, `retrieving section methodology summary for ${client} ${project}`, {
                showToast: false,
            });
        }
    }

    async bulkToggleSectionMethodology(client, project, methodology) {
        try {
            const repsonse = await axios.get(
                `${config.calibrationPlatformAPIUrl}/maple/get/sections/bulk-toggle-methodology/client/${client}/project/${project}/methodology/${methodology}`,
                {
                    headers: this.getRequestHeaders(),
                }
            );
            return repsonse.data;
        } catch (error) {
            handleError(error, `toggling section methodology for ${client} ${project} to ${methodology}`, {
                showToast: false,
            });
        }
    }

    async generatePalmInputs(client, project, version_dttm) {
        try {
            const response = await axios.post(
                `${config.calibrationPlatformAPIUrl}/processes/generate-palm-inputs`,
                {
                    client_name: client,
                    project_name: project,
                    inputs: [
                        {
                            dataset_type: "palm-simulation-areas",
                            group_key: DEFAULT_GROUP_KEY,
                            version_dttm: version_dttm,
                        },
                        {
                            dataset_type: "palm-simulation-spans",
                            group_key: DEFAULT_GROUP_KEY,
                            version_dttm: version_dttm,
                        },
                    ],
                    email_list: [JSON.parse(localStorage.getItem("user")).email],
                    extra: {},
                },
                {
                    headers: this.getRequestHeaders(),
                }
            );
            return response.data;
        } catch (error) {
            handleError(error, "generating PALM inputs", { throws: "error" });
        }
    }

    async enqueuePalmJob(client, project, job_version_dttm, simulation_areas) {
        try {
            const response = await axios.post(
                `${config.calibrationPlatformAPIUrl}/processes/enqueue-palm-jobs`,
                {
                    client_name: client,
                    project_name: project,
                    inputs: [
                        {
                            dataset_type: "palm-jobs",
                            group_key: DEFAULT_GROUP_KEY,
                            version_dttm: job_version_dttm,
                        },
                    ],
                    email_list: [JSON.parse(localStorage.getItem("user")).email],
                    extra: {
                        simulation_area_ids: simulation_areas,
                    },
                },
                {
                    headers: this.getRequestHeaders(),
                }
            );
            return response.data;
        } catch (error) {
            handleError(error, "enqueuing PALM jobs", { throws: "error" });
        }
    }

    async queryVisibleSpans(client, project, versionDttm, latitude, longitude, angle, distance, fov) {
        try {
            const result = await axios.get(
                `${config.calibrationPlatformAPIUrl}/query-visible-spans/clients/${client}/projects/${project}/groups/default/files/${versionDttm}/`,
                {
                    headers: this.getRequestHeaders(),
                    params: {
                        latitude: latitude,
                        longitude: longitude,
                        angle: angle,
                        distance: distance,
                        fov: fov,
                    },
                }
            );
            return result.data;
        } catch (error) {
            handleError(
                error,
                `querying visible spans for client ${client}, project ${project}, version: ${versionDttm}, position: ${latitude},${longitude}, angle: ${angle}, distance: ${distance}, fov: ${fov}`,
                { throws: "error" }
            );
        }
    }
    async submitDeviceChange(client, project, currentDeviceDatasetVersionDttm, deviceModificationPayload) {
        try {
            // Build inputs - include version_dttm if we have it for optimistic locking
            const inputs = currentDeviceDatasetVersionDttm
                ? [
                    {
                        dataset_type: "device-data",
                        group_key: DEFAULT_GROUP_KEY,
                        version_dttm: currentDeviceDatasetVersionDttm,
                    },
                ]
                : [];

            const requestBody = {
                client_name: client,
                project_name: project,
                inputs: inputs,
                email_list: [JSON.parse(localStorage.getItem("user")).email],
                extra: deviceModificationPayload,
            };

            console.log("Device change request:", JSON.stringify(requestBody, null, 2));

            await axios.post(`${config.calibrationPlatformAPIUrl}/processes/device-data-modification`, requestBody, {
                headers: this.getRequestHeaders(),
            });
        } catch (error) {
            handleError(
                error,
                `submitting device change for client ${client}, project ${project}, current version ${currentDeviceDatasetVersionDttm} and payload ${JSON.stringify(
                    deviceModificationPayload
                )}`
            );
        }
    }
    async submitEdgeDeviceAdd(
        client,
        project,
        currentDeviceDatasetVersionDttm,
        deviceToAddPayload,
        skipMasterdataSync = false
    ) {
        return this.submitDeviceChange(client, project, currentDeviceDatasetVersionDttm, {
            add_edge_device: deviceToAddPayload,
            skip_masterdata_sync: skipMasterdataSync,
        });
    }
    async submitWeatherSensorAdd(
        client,
        project,
        currentDeviceDatasetVersionDttm,
        weatherSensorToAddPayload,
        skipMasterdataSync = false
    ) {
        return this.submitDeviceChange(client, project, currentDeviceDatasetVersionDttm, {
            add_weather_sensor: weatherSensorToAddPayload,
            skip_masterdata_sync: skipMasterdataSync,
        });
    }

    async submitEdgeDeviceEdit(
        client,
        project,
        currentDeviceDatasetVersionDttm,
        edgeDeviceId,
        editPayload,
        skipMasterdataSync = false
    ) {
        return this.submitDeviceChange(client, project, currentDeviceDatasetVersionDttm, {
            edit_edge_device: {
                [edgeDeviceId]: editPayload,
            },
            skip_masterdata_sync: skipMasterdataSync,
        });
    }
    submitWeatherSensorEdit(
        client,
        project,
        currentDeviceDatasetVersionDttm,
        weatherSensorSn,
        editPayload,
        skipMasterdataSync = false
    ) {
        return this.submitDeviceChange(client, project, currentDeviceDatasetVersionDttm, {
            edit_weather_sensor: {
                [weatherSensorSn]: editPayload,
            },
            skip_masterdata_sync: skipMasterdataSync,
        });
    }
    async submitEdgeDeviceDelete(
        client,
        project,
        currentDeviceDatasetVersionDttm,
        edgeDeviceId,
        skipMasterdataSync = false
    ) {
        return this.submitDeviceChange(client, project, currentDeviceDatasetVersionDttm, {
            delete_edge_device: edgeDeviceId,
            skip_masterdata_sync: skipMasterdataSync,
        });
    }
    submitWeatherSensorDelete(
        client,
        project,
        currentDeviceDatasetVersionDttm,
        weatherSensorSn,
        skipMasterdataSync = false
    ) {
        return this.submitDeviceChange(client, project, currentDeviceDatasetVersionDttm, {
            delete_weather_sensor: weatherSensorSn,
            skip_masterdata_sync: skipMasterdataSync,
        });
    }
    async submitDeviceDatasetOnlineDateOverride(
        client,
        project,
        currentDeviceDatasetVersionDttm,
        onlineDateOverride,
        skipMasterdataSync = false
    ) {
        return this.submitDeviceChange(client, project, currentDeviceDatasetVersionDttm, {
            set_online_date_override: onlineDateOverride,
            skip_masterdata_sync: skipMasterdataSync,
        });
    }
    async extractCoefficients(client, project, simulationSpanVersionDttm, deviceDatasetVersionDttm) {
        let inputDatasets = [];
        if (simulationSpanVersionDttm) {
            inputDatasets.push({
                dataset_type: "palm-simulation-spans",
                group_key: DEFAULT_GROUP_KEY,
                version_dttm: simulationSpanVersionDttm,
            });
        }
        if (deviceDatasetVersionDttm) {
            inputDatasets.push({
                dataset_type: "device-data",
                group_key: DEFAULT_GROUP_KEY,
                version_dttm: deviceDatasetVersionDttm,
            });
        }
        try {
            await axios.post(
                `${config.calibrationPlatformAPIUrl}/processes/extract-palm-coefficients`,
                {
                    client_name: client,
                    project_name: project,
                    inputs: inputDatasets,
                    email_list: [JSON.parse(localStorage.getItem("user")).email],
                    extra: {},
                },
                {
                    headers: this.getRequestHeaders(),
                }
            );
        } catch (error) {
            handleError(error, "requesting PALM coefficients", { throws: "error" });
        }
    }

    async getRunningProcessesByClientAndProject(client, project, processTypes = []) {
        try {
            const params = {};
            if (processTypes.length > 0) {
                params.process_types = processTypes.join(",");
            }
            const result = await axios.get(
                `${config.calibrationPlatformAPIUrl}/clients/${client}/projects/${project}/running/`,
                {
                    headers: this.getRequestHeaders(),
                    params: params,
                }
            );

            return result.data;
        } catch (error) {
            handleError(error, "fetching running processes", { showToast: false, throws: "error" });
        }
    }

    /**
     * submit a POST request to set an array of one or more process_run_ids as failed
     * @param {string} processRunIds
     * @returns {Array[ProcessRun]} Array of ProcessRun rows updated from PostGRES
     */
    async setSelectedProcessesFailed(processRunIds) {
        try {
            const result = await axios.post(
                `${config.calibrationPlatformAPIUrl}/setfailed/`,
                { run_ids: processRunIds },
                {
                    headers: this.getRequestHeaders(),
                }
            );
            console.log(result.data);
            return;
        } catch (error) {
            handleError(error, "setting processes as failed", { throws: "error" });
        }
    }

    async setSelectedDatasetAsLatest(client, project, group_key, dataset_type, dttm) {
        try {
            const result = await axios.post(
                `${config.calibrationPlatformAPIUrl}/setdatasetaslatest/`,
                [
                    {
                        client_name: client,
                        project_name: project,
                        group_key: group_key,
                        dataset_type: dataset_type,
                        version_dttm: dttm,
                    },
                ],
                {
                    headers: this.getRequestHeaders(),
                }
            );
            console.log(result.data);
            return;
        } catch (error) {
            handleError(error, "setting the latest dataset", { throws: "error" });
        }
    }

    async getAllClientInfoByKeys(client, project, dataset_type, group_key) {
        try {
            const result = await axios.get(
                `${config.calibrationPlatformAPIUrl}/getdatasets/${client}/projects/${project}/dataset-types/${dataset_type}/groups/${group_key}/`,
                {
                    headers: this.getRequestHeaders(),
                }
            );
            return result.data;
        } catch (error) {
            handleError(error, "fetching all client info by keys", { showToast: false, throws: "error" });
        }
    }

    async triggerAspenInputPipeline(clientName, projectName, extra, inputs) {
        try {
            const body = {
                client_name: clientName,
                project_name: projectName,
                inputs: inputs,
                email_list: [JSON.parse(localStorage.getItem("user")).email],
                extra: extra,
            };

            const response = await axios.post(
                `${config.calibrationPlatformAPIUrl}/processes/trigger-aspen-inputs`,
                body,
                {
                    headers: this.getRequestHeaders(),
                }
            );

            showGlobalSuccessToast("Submitted. Check Progress Viewer for status.");
            return response;
        } catch (error) {
            handleError(error, "triggering ASPEN input pipeline", { throws: "none" });
        }
    }

    async AnalyzeSensorCount(clientName, projectName, failureRate, maxNumSensors, sensorPlacementInputVersionDttm) {
        const requestBody = {
            client_name: clientName,
            project_name: projectName,
            email_list: [JSON.parse(localStorage.getItem("user")).email],
            extra: { annual_failure_rate: failureRate, max_num_sensors: maxNumSensors },
            inputs: [{ dataset_type: "sensor-placement-input", group_key: DEFAULT_GROUP_KEY, version_dttm: sensorPlacementInputVersionDttm }],
        };
        try {
            const response = await axios.post(
                `${config.calibrationPlatformAPIUrl}/processes/analyze-sensor-count`,
                requestBody,
                { headers: this.getRequestHeaders() }
            );
            return response.data;
        } catch (error) {
            handleError(error, "generating sensor count analysis", { throws: "none" });
        }
    }

    async submitSensorPlacementRefinement(
        client,
        project,
        sensorPlacementVersionDttm,
        sensorInputVersionDttm,
        updates
    ) {
        const requestBody = {
            client_name: client,
            project_name: project,
            email_list: [JSON.parse(localStorage.getItem("user")).email],
            inputs: [
                {
                    dataset_type: "sensor-placement-options",
                    group_key: DEFAULT_GROUP_KEY,
                    version_dttm: sensorPlacementVersionDttm,
                },
                {
                    dataset_type: "sensor-placement-input",
                    group_key: DEFAULT_GROUP_KEY,
                    version_dttm: sensorInputVersionDttm,
                },
            ],
            extra: updates,
        };

        try {
            const response = await axios.post(
                `${config.calibrationPlatformAPIUrl}/processes/refine-sensor-placement-options`,
                requestBody,
                { headers: this.getRequestHeaders() }
            );
            return response.data;
        } catch (error) {
            handleError(
                error,
                `refining sensor placement options for dataset version ${sensorPlacementVersionDttm} with updates ${JSON.stringify(
                    updates
                )}`,
                { throws: "none" }
            );
        }
    }

    async createSensorFieldPlan(client, project, versionDttm) {
        const requestBody = {
            client_name: client,
            project_name: project,
            email_list: [JSON.parse(localStorage.getItem("user")).email],
            inputs: [
                {
                    dataset_type: "sensor-placement-options",
                    group_key: DEFAULT_GROUP_KEY,
                    version_dttm: versionDttm,
                },
            ],
        };

        try {
            const response = await axios.post(
                `${config.calibrationPlatformAPIUrl}/processes/create-sensor-field-plan`,
                requestBody,
                { headers: this.getRequestHeaders() }
            );
            showGlobalSuccessToast("Sensor field plan was created successfully.");
            return response.data;
        } catch (error) {
            handleError(error, `creating sensor field plan for dataset version ${versionDttm}`, {
                throws: "none",
            });
        }
    }

    async triggerMaple(clientName, projectName, extra) {
        try {
            const body = {
                client_name: clientName,
                project_name: projectName,
                inputs: [],
                email_list: [JSON.parse(localStorage.getItem("user")).email],
                extra: extra,
            };
            const response = await axios.post(`${config.calibrationPlatformAPIUrl}/processes/trigger-maple`, body, {
                headers: this.getRequestHeaders(),
            });
            return response;
        } catch (error) {
            handleError(error, "triggering Maple");
        }
    }

    async checkBamStatus(client, project, versionDttm) {
        try {
            const result = await axios.get(
                `${config.calibrationPlatformAPIUrl}/maple/check-bam-status/clients/${client}/projects/${project}/version-dttm/${versionDttm}`,
                { headers: this.getRequestHeaders() }
            );
            return result.data;
        } catch (error) {
            if (error?.response?.status === 404) return null;
            handleError(error, "checking BAM status");
        }
    }

    async bamUpload(clientName, projectName, versionDttm, useEpoch = false, calibrationDttm = null) {
        try {
            const extra = {};
            if (useEpoch) {
                extra.use_epoch = true;
            } else if (calibrationDttm) {
                extra.calibration_dttm = calibrationDttm;
            }
            const body = {
                client_name: clientName,
                project_name: projectName,
                inputs: [
                    { dataset_type: "maple-span-bams", group_key: "default", version_dttm: versionDttm },
                    { dataset_type: "maple-weather-site-bams", group_key: "default", version_dttm: versionDttm },
                ],
                extra,
            };
            const response = await axios.post(`${config.calibrationPlatformAPIUrl}/processes/bam-upload`, body, {
                headers: this.getRequestHeaders(),
            });
            return response;
        } catch (error) {
            handleError(error, "uploading BAM results");
        }
    }

    async promoteOrDemoteAllDatasetsByProcessRunId(processRunId, direction) {
        try {
            const result = await axios.post(
                `${config.calibrationPlatformAPIUrl}/process-runs/pro-or-demote-all-by-process-run-id`,
                {
                    process_run_id: processRunId,
                    direction: direction,
                },
                {
                    headers: this.getRequestHeaders(),
                }
            );
            console.log(result.data);
            return result.data;
        } catch (error) {
            const actionWord = direction === "promote" ? "promoting" : "demoting";
            handleError(error, `${actionWord} datasets for process run ${processRunId}`, { throws: "error" });
        }
    }
}

export const calibrationService = new CalibrationService();
